import { Suspense, lazy } from "react";
import PerformancePanel from "@/components/PerformancePanel";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Hreflang from "./components/Hreflang";

// Reload once if a dynamic chunk fails to load (typically after a new deploy
// invalidates the previous hashed filenames the open tab is still referencing).
const lazyWithReload = <T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      const key = "lovable:chunk-reload";
      if (typeof window !== "undefined" && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        // Return a never-resolving promise while the page reloads.
        return await new Promise<T>(() => {});
      }
      throw err;
    }
  });

// Lazy load pages to reduce initial bundle size and improve FID
const Index = lazyWithReload(() => import("./pages/Index"));
const CollegeRecruitment = lazyWithReload(() => import("./pages/CollegeRecruitment"));
const CollegeStats = lazyWithReload(() => import("./pages/CollegeStats"));
const AdminLogin = lazyWithReload(() => import("./pages/AdminLogin"));
const AdminDashboard = lazyWithReload(() => import("./pages/AdminDashboard"));
const AdminSetup = lazyWithReload(() => import("./pages/AdminSetup"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Geo-optimization hreflang/locale meta */}
        <Hreflang />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/college-recruitment" element={<CollegeRecruitment />} />
            <Route path="/college-stats" element={<CollegeStats />} />
            <Route path="/admin/setup" element={<AdminSetup />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    <PerformancePanel />
  </QueryClientProvider>
);

export default App;
